import React from 'react'
import {useState, useEffect} from 'react'
import {useShopperBasketsMutationHelper} from '@salesforce/commerce-sdk-react'
import {useCustomToast} from '../../../contexts/custom-toast-context'
import {useMiniCartContext} from '../../../contexts/mini-cart-context'
import {useAddToCartConfirmation} from '../../../contexts/add-to-cart-confirmation-context'
import {useWishlistActions} from '../../../hooks/use-wishlist-actions'
import {sendItemCartEvent, setTempRecommendation} from '../../../utils/gtm-utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

const useQuickViewCart = ({
    product,
    variant,
    quantity,
    isDesktopViewport,
    isWishlistPage,
    wishlistProductId,
    setIsQuickViewOpen,
    primaryCategory,
    hasUnselectedVariations,
    setShowOptionsMessage,
    onMissingVariations,
    intl,
    onAddToCartSuccess,
    onQuickViewClose
}) => {
    const [addToCartError, setAddToCartError] = useState('')

    useEffect(() => {
        setAddToCartError('')
    }, [variant?.productId])

    const {showCustomToast, removeToast} = useCustomToast()
    const navigate = useNavigation()
    const {removeItemFromWishlist} = useWishlistActions()
    const {onOpen: onAddToCartModalOpen, onAutoClose: onAddToCartModalClose} = useMiniCartContext()
    const {openConfirmation} = useAddToCartConfirmation()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()

    // Handle adding items to cart
    const getMatchingGroup = (productInput) => {
        const matchingGroup = productInput?.variationGroups?.find(
            (group) => group.variationValues.color === productInput.variationValues.color
        )

        if (matchingGroup) {
            return matchingGroup.productId
        }

        return null
    }

    const handleAddToCart = async (productSelectionValues) => {
        try {
            const productItems = productSelectionValues.map(({product: itemProduct, variant: itemVariant, quantity: itemQuantity}) => ({
                productId: itemVariant.productId,
                productName: itemProduct?.name,
                price: itemVariant.price,
                c_brand: itemProduct?.brand,
                c_currency: itemProduct?.currency,
                c_categories: itemProduct?.c_categories,
                c_refinementColor: itemProduct?.c_refinementColor,
                c_size: itemProduct?.c_size || itemVariant.variationValues?.size,
                c_variationGroup: getMatchingGroup(itemProduct),
                quantity: itemQuantity
            }))

            // GTM Modification
            if (primaryCategory) {
                setTempRecommendation(primaryCategory)
            }

            await addItemToNewOrExistingBasket(productItems)

            // GTM Modification
            sendItemCartEvent(productItems[0], false)

            return productSelectionValues
        } catch (error) {
            console.error('The error occured while adding a product to the basket: ', error)
        }
    }

    // Handle cart item addition with validation
    const handleCartItem = async () => {
        if (hasUnselectedVariations) {
            setShowOptionsMessage(true)
            if (onMissingVariations) {
                onMissingVariations()
            }
            return
        }

        try {
            const itemsAdded = await handleAddToCart([{product, variant, quantity}])

            if (itemsAdded) {
                setAddToCartError('')
                if (onAddToCartSuccess) {
                    onAddToCartSuccess()
                }

                // Remove from wishlist if on wishlist page
                // Use wishlistProductId (the original ID that was saved in wishlist)
                // Not productId/pid/product.id because those change when user selects different variants
                if (isWishlistPage && wishlistProductId) {
                    const productObj = {productId: wishlistProductId}
                    await removeItemFromWishlist(productObj, null, 'add-to-cart-context')
                }

                setIsQuickViewOpen(false)
                openConfirmation({product, variant, quantity})
            } else {
                setAddToCartError(
                    intl.formatMessage({
                        id: 'product_view.error.not_added_to_cart',
                        defaultMessage: 'Maximum quantity has been reached'
                    })
                )
            }
        } catch (e) {
            console.log(e)
        }
    }

    return {
        handleCartItem,
        addToCartError
    }
}

export default useQuickViewCart
